import { Immutable } from "immer";
import React, { useEffect, useRef } from "react";
import { t } from "ttag";

import type { LimitedTimeOfferGridBlock as Value } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { isElementVisible } from "../../utils/visibility";
import { Flair } from "../Flair";
import { OfferModal } from "../OfferModal";

type IProps = Immutable<{
    value: Value;
}>;

type Tile = NonNullable<Value["tiles"]>[number];

// --- Tracking (port of tsicommon_tracker_tags.py for this block) -------------
//
// The legacy `limited-time-offer-grid-block.html` rendered each tile with two
// Django tags scoped to the tile StructValue:
//   {% include_block tile with
//        promotion_tracking_value=tile
//        promotion_tracking_block_name='limited-time-offer-grid-block'
//        promotion_tracking_block_index=forloop.counter
//        promotion_tracking_fallback_name='Limited Time Offer Grid' %}
//   ...which the cta_link (link-block.html) turned into a
//   {% select_promotion_tracking_attrs %} onclick, plus a
//   {% track_view_promotion %} fire-on-render at the bottom of the tile.
//
// Phase 2 removes those server wrappers, so both the `view_promotion`
// (fire-on-mount) and `select_promotion` (cta_link click) GA4 events are
// reproduced here. `block_index` is 1-based per tile (forloop.counter), so the
// id carries the `-{index}` suffix and `creative_slot` is per-tile — unlike
// HeroImageBlock (whose block_index was always None).

const BLOCK_NAME = "limited-time-offer-grid-block";
const FALLBACK_NAME = "Limited Time Offer Grid";

const stripHtml = (html: string): string => {
    if (!html) {
        return "";
    }
    const text = html.replace(/<[^>]*>/g, " ");
    return text.replace(/\s+/g, " ").trim();
};

// Mirror of django.utils.text.slugify (allow_unicode=False).
const slugify = (value: string): string =>
    value
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");

/**
 * Port of `_slugify_tracking_candidates`. For each candidate: strip HTML tags
 * (mirroring `_extract_text_from_richtext` → `strip_tags`), replace URL
 * separators `[:/.]` with spaces so they become hyphens rather than being
 * stripped, then slugify. Drop empties and duplicates, then join with "-".
 */
const slugifyTrackingCandidates = (
    candidates: readonly string[],
    fallback: string,
): string => {
    const parts: string[] = [];
    const seen = new Set<string>();
    for (const candidate of candidates) {
        const normalized = stripHtml(candidate).replace(/[:/.]/g, " ");
        const slug = slugify(normalized);
        if (!slug || seen.has(slug)) {
            continue;
        }
        parts.push(slug);
        seen.add(slug);
    }
    return parts.join("-") || fallback;
};

type PromotionEvent = Immutable<{
    event: "view_promotion" | "select_promotion";
    creative_name: string;
    creative_slot: string;
    promotion_id: string;
    promotion_name: string;
    items: Tile["view_promotion_items"];
}>;

const buildPromotionEvent = (
    tile: Immutable<Tile>,
    blockIndex: number,
    eventName: "view_promotion" | "select_promotion",
): PromotionEvent => {
    // `_normalize_tracking_name_id`: first non-empty name candidate wins. For a
    // tile the first populated candidate is `header_text`; falls back to
    // `fallback_name` ("Limited Time Offer Grid") when header_text is empty.
    const headerText = stripHtml(tile.header_text ?? "");
    const promotionName = headerText || FALLBACK_NAME;

    // `_build_promotion_metadata`: promotion_id is the slugified join of
    // [promotion_name, ...extra_id_candidates], suffixed with `-{block_index}`.
    // The tile template passes no custom `extra_id_candidates`, so the legacy
    // default list applies (with `value` = the tile struct): header_text,
    // page.url, cta_link.page.url, cta_link.external_url, linked_offer.url,
    // link.url. The tile has no `linked_offer`/`link` fields, so those resolve
    // to None and drop out — but the CTA link URLs (cta_link.page.url and
    // cta_link.external_url) MUST be included so external-link tiles produce the
    // same GA4 promotion_id as the legacy server tracker.
    const idBase = slugifyTrackingCandidates(
        [
            promotionName,
            tile.header_text ?? "",
            tile.page?.url ?? "",
            tile.cta_link?.page?.url ?? "",
            tile.cta_link?.external_url ?? "",
        ],
        slugify(promotionName) || slugify(BLOCK_NAME) || "tracking-block",
    );

    return {
        event: eventName,
        creative_name: promotionName,
        creative_slot: `${BLOCK_NAME}-${blockIndex}`,
        promotion_id: `${idBase}-${blockIndex}`,
        promotion_name: promotionName,
        // `view_promotion_items` is serialized server-side from
        // `cta_link`'s target page (see `LimitedTimeOfferTile.get_data_for_
        // client_render`), mirroring the legacy template's
        // `get_link_products_for_tracking self.cta_link as
        // promotion_tracking_products` lookup that fed `entries=` into
        // `_build_promotion_event`.
        items: tile.view_promotion_items,
    };
};

const isDoNotTrackEnabled = (win: Window): boolean => {
    // Assigning `Navigator` to a type with extra optional members type-checks
    // (the extras are optional), so this widening needs no `as` cast.
    const nav: Navigator & {
        msDoNotTrack?: string;
        globalPrivacyControl?: boolean;
    } = win.navigator;
    return (
        nav.doNotTrack === "1" ||
        nav.msDoNotTrack === "1" ||
        nav.globalPrivacyControl === true
    );
};

const pushPromotionEvent = (
    tile: Immutable<Tile>,
    blockIndex: number,
    eventName: "view_promotion" | "select_promotion",
): void => {
    const win = getWindow();
    if (!win) {
        return;
    }
    // Mirror the Django tags' DNT guard (`request.headers["DNT"] == "1"`).
    if (isDoNotTrackEnabled(win)) {
        return;
    }
    // Mirror the legacy push_to_datalayer.html flush + push.
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push({ items: null });
    win.dataLayer.push(buildPromotionEvent(tile, blockIndex, eventName));
};

// --- Per-tile component ------------------------------------------------------

type TileProps = Immutable<{
    tile: Tile;
    // 1-based position of the tile (legacy `forloop.counter`), used for the GA4
    // `creative_slot` / `promotion_id` index suffix.
    blockIndex: number;
}>;

const LimitedTimeOfferTileBlock = (
    props: TileProps,
): React.JSX.Element | null => {
    const tile = props.tile;
    const blockIndex = props.blockIndex;

    const hasFiredView = useRef(false);
    const rootRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        // Fire `view_promotion` once on mount, mirroring the tile template's
        // {% track_view_promotion %} impression.
        if (hasFiredView.current) {
            return;
        }
        // The promo gate below short-circuits render to null, but this effect
        // still runs (and `rootRef.current` would be null, so the visibility
        // gate below would pass), so re-check the gate before emitting the view.
        // Mirrors HeroImageBlock's in-effect promo re-check.
        if (tile.promo_period && !tile.promo_period.is_promo_active) {
            return;
        }
        // Gate on visibility like the Django tag / PromotionBlock: a hidden tile
        // (e.g. ancestor `display: none`) must not emit an impression.
        const win = getWindow();
        if (!win || !isElementVisible(rootRef.current)) {
            return;
        }
        // Set the fire-once guard only once the impression is actually emitted
        // (NOT at the top of the effect): the guard must protect the FIRE, not
        // the attempt, so a tile gated out here (promo inactive / hidden) that
        // later re-mounts visible can still fire its impression.
        hasFiredView.current = true;
        pushPromotionEvent(tile, blockIndex, "view_promotion");
        // Fire-on-mount only: intentionally empty deps. exhaustive-deps is not
        // enabled in this codebase, so a disable annotation would be flagged.
    }, []);

    // Promo gate: reproduce `{% if not value.promo_period or
    // value.promo_period.is_promo_active %}`.
    if (tile.promo_period && !tile.promo_period.is_promo_active) {
        return null;
    }

    const image = tile.image;
    const ctaLink = tile.cta_link;
    const offerDetailURL = tile.page?.url ?? "";

    return (
        <div
            ref={rootRef}
            className="limited-time-offer-tile-block"
            role="group"
            aria-label={t`item`}
        >
            {tile.flair?.content ? (
                <Flair
                    flair={{
                        style: tile.flair.style ?? null,
                        content: tile.flair.content,
                    }}
                />
            ) : null}
            {image ? (
                <img
                    src={image.renditions["height-450"]?.url ?? image.url}
                    alt={image.default_alt_text}
                    loading="lazy"
                    decoding="async"
                />
            ) : null}
            <div>
                {/* Legacy `{{ self.pre_header_text|richtext }}`: already-rendered
                    rich-text HTML, injected directly. */}
                <h6
                    dangerouslySetInnerHTML={{
                        __html: tile.pre_header_text ?? "",
                    }}
                />
                <div className="limited-time-offer-tile-block__header">
                    {/* Legacy passed `cta_text=self.header_text|richtext`;
                        OfferModal dangerouslySetInnerHTMLs ctaText, so pass the
                        rendered HTML raw. */}
                    <OfferModal
                        offerDetailURL={offerDetailURL}
                        ctaText={tile.header_text ?? undefined}
                        ctaArrow={false}
                        isButton={false}
                    />
                </div>
                <div
                    className="limited-time-offer-tile-block__description"
                    dangerouslySetInnerHTML={{ __html: tile.description ?? "" }}
                />
                {ctaLink ? (
                    // Port of link-block.html: href = page.url ?? external_url
                    // ?? "#", `class="button"` when is_button, display_name as
                    // text, and the {% select_promotion_tracking_attrs %} onclick
                    // reproduced as an onClick handler.
                    <a
                        href={ctaLink.page?.url ?? ctaLink.external_url ?? "#"}
                        className={ctaLink.is_button ? "button" : undefined}
                        onClick={() =>
                            pushPromotionEvent(
                                tile,
                                blockIndex,
                                "select_promotion",
                            )
                        }
                    >
                        {ctaLink.display_name}
                    </a>
                ) : null}
                <div className="limited-time-offer-tile-block__cta">
                    <OfferModal
                        offerDetailURL={offerDetailURL}
                        ctaText=""
                        ctaArrow={false}
                        isButton={false}
                    />
                </div>
            </div>
            <div className="limited-time-offer-tile-block__arrow-container">
                <OfferModal
                    offerDetailURL={offerDetailURL}
                    ctaText=""
                    ctaArrow={true}
                    isButton={false}
                />
            </div>
        </div>
    );
};

// --- Main component ----------------------------------------------------------

/**
 * Faithful port of the legacy server-rendered `limited-time-offer-grid-block.html`
 * (+ its `limited-time-offer-tile-block.html` sub-block). Each tile renders an
 * optional Flair, a `height-450` Wagtail image, a pre-header, an OfferModal
 * trigger fed the rendered header_text HTML, a rich-text description, the
 * cta_link, and the empty-ctaText OfferModal triggers (the "See Details" CTA and
 * the arrow). Per-tile GA4 `view_promotion` (on mount) / `select_promotion` (on
 * cta_link click) events are reproduced from tsicommon_tracker_tags.py.
 */
export const LimitedTimeOfferGridBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    return (
        <section className="limited-time-offer-grid-block">
            <div className="l-capped-width">
                {(v.tiles ?? []).map((tile, i) => (
                    // Index key: ListBlock tiles don't surface a stable id, so a
                    // CMS reorder reuses positional DOM — matches the legacy
                    // `forloop`-positional render.
                    <LimitedTimeOfferTileBlock
                        key={i}
                        tile={tile}
                        blockIndex={i + 1}
                    />
                ))}
            </div>
        </section>
    );
};
